'use client'

import clsx from 'clsx'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isToday from 'dayjs/plugin/isToday'
import relativeTime from 'dayjs/plugin/relativeTime'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { flatten, map, sortBy, unionWith, uniqWith } from 'lodash'
import { useMemo } from 'react'
import { useLocale, useTranslations } from '@/lib/next-shim'

import { CalendarIcon, ChevronDownIcon, MapPinIcon } from '@heroicons/react/20/solid'
import { CreditCardIcon } from '@heroicons/react/24/outline'
import { Disclosure } from '@headlessui/react'

import { AvailabilityMeetingPlace, GetMeetingLinkQuery } from '@/core/graphql/types'

import 'dayjs/locale/en'
import 'dayjs/locale/fr'

dayjs.extend(customParseFormat)
dayjs.extend(isSameOrAfter)
dayjs.extend(isToday)
dayjs.extend(relativeTime)
dayjs.extend(timezone)
dayjs.extend(utc)

type SpecialitiesProps = {
  meetingLink: GetMeetingLinkQuery['meetingLink']
  title: string
}

export function Specialities(props: SpecialitiesProps) {
  const { meetingLink, title } = props
  const lang = useLocale()
  const t = useTranslations()

  const specialityAvailabilities = useMemo(() => {
    return meetingLink.specialities.reduce((result: Record<string, (typeof meetingLink)['availabilities']>, speciality) => {
      if (!(speciality.code in result)) {
        result[speciality.code] = meetingLink.availabilities.filter((avaialbility) => avaialbility.specialities.includes(speciality.name))
      }
      return result
    }, {})
  }, [meetingLink])

  return (
    <div className="bg-white shadow rounded-md sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h2 className="text-lg font-medium leading-6 text-gray-900">{title}</h2>
      </div>
      <div className="border-t border-gray-200">
        <ul role="list" className="divide-y divide-gray-200">
          {meetingLink.specialities.map((speciality) => (
            <li key={speciality.code}>
              <Disclosure>
                {({ open }) => (
                  <>
                    <Disclosure.Button as="div" className="cursor-pointer flex items-center px-4 py-4 sm:px-6">
                      <div className="min-w-0 flex-1 flex items-start justify-between">
                        <div className="truncate">
                          <h3 className="text-sm font-medium text-gray-900">{speciality.name}</h3>
                        </div>
                        <p className="flex items-center sm:justify-end text-sm text-gray-900">
                          <CreditCardIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                          {speciality.price} €
                        </p>
                      </div>
                      <div className="ml-5 flex items-center flex-shrink-0">
                        <ChevronDownIcon className={clsx({ 'rotate-180 transform': open }, 'h-5 w-5 text-gray-400')} aria-hidden="true" />
                      </div>
                    </Disclosure.Button>
                    <Disclosure.Panel className="px-4 pb-4 sm:px-6 text-sm text-gray-900">
                      <div className="divide-y divide-gray-300 ring-1 ring-gray-300 rounded-md">
                        {map(specialityAvailabilities[speciality.code], (availability) => {
                          const currentRecurrenceDay = availability.recurrenceDays.find(({ day }) => dayjs().day() === day)
                          return (
                            <div key={availability._id} className="px-4 py-4">
                              <div className="grid grid-cols-1 gap-2">
                                <p className="flex items-center text-sm text-gray-900">
                                  <MapPinIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                                  {availability.meetingPlace === AvailabilityMeetingPlace.Center && availability.address?.description}
                                  {availability.meetingPlace === AvailabilityMeetingPlace.AtHome &&
                                    t('appointment.meetingLink.Specialities.availability.isAtHome')}
                                  {availability.meetingPlace === AvailabilityMeetingPlace.VisioConference &&
                                    t('appointment.meetingLink.Specialities.availability.visioConference')}
                                </p>
                                <div className="flex items-start justify-start text-sm text-gray-900">
                                  <CalendarIcon className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                                  <Disclosure as="div" className="w-full">
                                    {({ open }) => (
                                      <>
                                        <div className="flex items-center mb-1">
                                          <p>{t('appointment.meetingLink.Specialities.availability.openingHours')}</p>
                                          <Disclosure.Button as="div" className="cursor-pointer ml-2 flex items-center flex-shrink-0">
                                            <ChevronDownIcon className={clsx({ 'rotate-180 transform': open }, 'h-5 w-5 text-gray-400')} aria-hidden="true" />
                                          </Disclosure.Button>
                                        </div>
                                        <Disclosure.Panel as="dl" static className="space-y-1 text-[12px] sm:text-sm">
                                          {availability.recurrenceDays.map(({ day: dayOfWeek, periods, timeZone }) => {
                                            const isToday = dayjs().day(dayOfWeek).isToday()
                                            const startOfDay = dayjs()
                                              .locale(lang)
                                              .day(dayOfWeek)
                                              .tz(timeZone || dayjs.tz.guess())
                                              .startOf('day')
                                            const scheduledDayPeriods = flatten(
                                              map(
                                                availability.scheduledDays.filter(
                                                  (scheduledDay) =>
                                                    dayjs(scheduledDay.day, scheduledDay.format).isSameOrAfter(dayjs(), 'day') &&
                                                    dayjs(scheduledDay.day, scheduledDay.format).day() === dayOfWeek
                                                ),
                                                (scheduledDay) =>
                                                  scheduledDay.periods.map((period) => ({
                                                    ...period,
                                                    day: scheduledDay.day,
                                                    format: scheduledDay.format,
                                                    isRecurrence: false,
                                                  }))
                                              )
                                            )
                                            const recurrencePeriods = periods.map((period) => ({ ...period, day: '', format: '', isRecurrence: true }))
                                            const totalPeriods = sortBy(
                                              unionWith(
                                                recurrencePeriods,
                                                scheduledDayPeriods,
                                                (arrVal, othVal) => arrVal.open === othVal.open && arrVal.close === othVal.close
                                              )
                                            )
                                            return !isToday && !open ? null : (
                                              <time key={`${availability._id}-${dayOfWeek}`} className="flex items-start justify-between">
                                                <dt className="capitalize">
                                                  {isToday && !open ? (
                                                    t('dayjs.today')
                                                  ) : (
                                                    <>
                                                      {dayjs().locale(lang).day(dayOfWeek).format('dddd')}{' '}
                                                      {dayOfWeek === currentRecurrenceDay?.day ? `(${t('dayjs.today').toLowerCase()})` : ''}
                                                    </>
                                                  )}
                                                </dt>
                                                <dd className="sm:w-1/2 lg:w-2/3 flex flex-col space-y-1 whitespace-nowrap">
                                                  {totalPeriods.length > 0
                                                    ? totalPeriods.map((period) => {
                                                        return !dayjs(period.day, period.format).isToday() && !period.isRecurrence && !open ? null : (
                                                          <p
                                                            key={`${availability._id}-${dayOfWeek}-${period.open}`}
                                                            className="flex flex-row justify-start sm:flex-row-reverse sm:justify-end"
                                                          >
                                                            {!period.isRecurrence && !!open && (
                                                              <span className="mr-1 sm:mr-0">{dayjs(period.day, period.format).format('(DD/MM/YY)')}</span>
                                                            )}
                                                            <span className="sm:mr-1">
                                                              {startOfDay.add(period.open, 'minute').format('HH[h]mm')}
                                                              {' - '}
                                                              {startOfDay.add(period.close, 'minute').format('HH[h]mm')}
                                                            </span>
                                                          </p>
                                                        )
                                                      })
                                                    : t('base.unavailable')}
                                                </dd>
                                              </time>
                                            )
                                          })}
                                        </Disclosure.Panel>
                                      </>
                                    )}
                                  </Disclosure>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
